import { FlexDivRow } from 'components/layout/flex'
import {
	type ChangeEvent,
	type ClipboardEvent,
	type KeyboardEvent,
	forwardRef,
	useEffect,
	useRef,
} from 'react'
import styled from 'styled-components'

type Props = {
	value: string
	onChange: (value: string) => void
	disabled?: boolean
	inputMode?: 'text' | 'numeric'
	pattern?: string
}

const OtpInput = forwardRef<HTMLInputElement, Props>(
	({ value, onChange, disabled, inputMode, pattern }, ref) => {
		const inputRefs = useRef<(HTMLInputElement | null)[]>([])

		useEffect(() => {
			if (ref && 'current' in ref) {
				ref.current = inputRefs.current[0]
			}
		}, [ref])

		useEffect(() => {
			if (!value && inputRefs.current[0]) {
				inputRefs.current[0].focus()
			}
		}, [value])

		const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value.replace(/[^0-9]/g, '')
			if (newValue.length > 1) return

			const newOtp = value.split('')
			newOtp[index] = newValue
			const finalValue = newOtp.join('')
			onChange(finalValue)

			if (newValue && index < 5) {
				inputRefs.current[index + 1]?.focus()
			}
		}

		const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Backspace') {
				if (value[index]) {
					const newOtp = value.split('')
					newOtp[index] = ''
					onChange(newOtp.join(''))
				} else if (index > 0) {
					inputRefs.current[index - 1]?.focus()
					const newOtp = value.split('')
					newOtp[index - 1] = ''
					onChange(newOtp.join(''))
				}
			}
		}

		const handlePaste = (index: number, e: ClipboardEvent) => {
			e.preventDefault()

			const pastedContent = e.clipboardData
				?.getData('text')
				.replace(/[^0-9]/g, '')
				.slice(0, 6)

			if (!pastedContent) return

			const currentOtp = value.split('')
			for (let i = 0; i < pastedContent.length; i++) {
				const targetIndex = index + i
				if (targetIndex < 6) {
					currentOtp[targetIndex] = pastedContent[i]
				}
			}

			const finalValue = currentOtp.join('')
			onChange(finalValue)

			const nextEmptyIndex = currentOtp.findIndex((digit, idx) => !digit && idx > index)
			if (nextEmptyIndex !== -1) {
				inputRefs.current[nextEmptyIndex]?.focus()
			} else {
				inputRefs.current[Math.min(index + pastedContent.length, 5)]?.focus()
			}
		}

		return (
			<OtpContainer columnGap="8px">
				{Array(6)
					.fill(0)
					.map((_, i) => (
						<StyledOtpInput
							// biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is safe here since OTP inputs have a fixed order
							key={i}
							ref={(el) => {
								inputRefs.current[i] = el
							}}
							type="text"
							maxLength={1}
							value={value[i] || ''}
							onChange={(e) => handleChange(i, e)}
							onKeyDown={(e) => handleKeyDown(i, e)}
							onPaste={(e) => handlePaste(i, e)}
							disabled={disabled}
							inputMode={inputMode}
							pattern={pattern}
						/>
					))}
			</OtpContainer>
		)
	}
)

const OtpContainer = styled(FlexDivRow)`
	width: fit-content;
	margin: 0 auto;
`
const StyledOtpInput = styled.input`
  width: 48px;
  height: 48px;
  text-align: center;
  font-size: 16px;
  padding: 15px;
  border: ${(props) => props.theme.colors.selectedTheme.border};
  border-radius: 8px;
  background: ${(props) => props.theme.colors.selectedTheme.input.background};
  color: ${(props) => props.theme.colors.selectedTheme.text.value};
  
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selectedTheme.button.primary.background};
  }
`

export default OtpInput
